import { arrayMove } from "../../../../utils/array-move"
import isEqual from "lodash/isEqual"
import { convertToBoolean, languages } from "../../../../utils"
import { ActionTypes } from "../../../../core/actions"
import ICreateField, {
  FieldDataType,
  IFieldReducers,
  patternDataTypeMatch,
  getDefaultCaratechValue,
  patternFormFieldTypeMatch,
  FormFieldTypes,
  IDropdownOption,
} from "./product-form-types"
import { CaratechType, ITechField, ITechRawData } from "../../../../models/bo"
import combineReducers, {
  Action,
  Reducer,
} from "../../../../utils/combine-reducer"
import { IProductFormActionTypesAndValues } from "../../_actions"
import { Multilang } from "../../../../models/product/multilang-string"
import { FieldType, IFileDetails, LockStatus } from "../../../../models"
import { EMPTY_ARRAY } from "../../../../utils/memo"

const getDefaultValue = <T extends FieldDataType>(
  defV: any,
  dataType: T,
  localized: boolean,
  isDeclinable: boolean
) => {
  switch (dataType) {
    case FieldDataType.Date:
      return new Date()
    case FieldDataType.Node:
      return { name: "", id: 0, code: "" }
    case FieldDataType.TreeState:
      return { checked: [], expanded: [] }
    default:
      return localized && !isDeclinable
        ? Multilang.String.CreateFrom(defV)
        : defV
  }
}

/**
 * Creates a field. Defaults are:
 * dataType: DataTypes.Text,
 * defaultValue: "",
 * localized: false,
 * formFieldType: FormFieldTypes.Input
 * fieldType: FieldType.Standard
 * isRequired: false
 * @param ICreateField
 */
function createField<T extends FieldDataType>(
  regenerateInfos: ICreateField<T>
) {
  const {
    label,
    code,
    dataType,
    localized = false,
    formFieldType = FormFieldTypes.Input,
    isRequired = false,
    isDeclinable = false,
    fieldType = FieldType.Standard,
    modified = false,
    lock = "unlock",
    defaultValue: defV = "",
  } = regenerateInfos

  const defaultValue = getDefaultValue(defV, dataType, localized, isDeclinable)

  const fields: IFieldReducers<T> = {
    value: getValueReducer(code, defaultValue, fieldType, formFieldType),
    code: (): string => code,
    localized: (): boolean => localized && !isDeclinable,
    formFieldType: (): FormFieldTypes => formFieldType,
    isRequired: (): boolean => isRequired,
    isDeclinable: (): boolean => isDeclinable,
    fieldType: (): FieldType => fieldType,
    label: (
      state = label,
      action: Action<IProductFormActionTypesAndValues>
    ): string => {
      switch (action.type) {
        case "CHANGE_FIELD_LABEL":
          return action.value[code] || state
        default:
          return state
      }
    },
    regenerateInfos: (
      state: ICreateField<T> = regenerateInfos || "",
      action: Action<IProductFormActionTypesAndValues>
    ): ICreateField<T> => {
      switch (action.type) {
        case "CHANGE_FIELD_VALUE":
          if (action.value.code === code && action.value.overrideDefaultValue) {
            return { ...state, defaultValue: action.value.fieldValue }
          } else {
            return state
          }
        case "STORE_TECH_FIELDS":
          if (fieldType === FieldType.Tech && action.value.techValues) {
            const t = action.value.techValues.find((v) => v.codeEnum === code)
            if (t) {
              return {
                ...state,
                defaultValue:
                  formFieldType === FormFieldTypes.Dropdown
                    ? t?.valueCode
                    : t?.value,
              }
            }
          }
          return state
        default:
          return state
      }
    },
    // Must return a stable reference: a new [] on every action would change every field (and every form
    // section) on each keystroke.
    options: (
      state: IDropdownOption[] = EMPTY_ARRAY,
      action: Action<IProductFormActionTypesAndValues>
    ) => {
      return formFieldType === FormFieldTypes.Dropdown
        ? regenerateInfos.options || EMPTY_ARRAY
        : EMPTY_ARRAY
    },
    errorText: (
      state = "",
      action: Action<IProductFormActionTypesAndValues>
    ): string => {
      // if (action.code !== code || action.fieldType !== fieldType) {
      //   return state
      // }

      let err = ""
      switch (action.type) {
        case "CHANGE_FIELD_VALUE":
          err = action.value.code === code ? action.value.errorMessage : state
          break
        case "CREATE_FILE":
          err = action.value.code === code ? "" : state
          break
        case "SET_ERROR_MESSAGE":
          err = action.value[code] || state
          break
        case "RESET_FORM":
          err = action.value.locked.includes(code) ? state : ""
          break
        default:
          err = state
          break
      }

      return err || ""
    },
    lock: (
      state = lock,
      action: Action<IProductFormActionTypesAndValues>
    ): LockStatus => {
      switch (action.type) {
        case "CHANGE_FIELD_VALUE":
          if (isTechAndMustLock(code, fieldType, action)) {
            return action.value.lock
          }
          return action.value.code === code ? action.value.lock || state : state
        default:
          return state
      }
    },
    modified: (
      state = modified,
      action: Action<IProductFormActionTypesAndValues>
    ): boolean => {
      switch (action.type) {
        case "STORE_TECH_FIELDS":
          if (fieldType === FieldType.Tech && action.value.modified) {
            return true
          }
          return state
        case "RESET_FORM":
          return false
        case "CHANGE_MODIFIED_STATE":
          if (isTechAndMustModify(fieldType, action)) {
            return action.value.modified
          }
          return action.value.code === code ? action.value.modified : state
        case "CHANGE_FIELD_VALUE":
          // if (isTechAndMustModify(code, fieldType, action)) {
          //   return true
          // }
          return action.value.code === code ? true : state
        case "SET_ERROR_MESSAGE":
        default:
          return state
      }
    },
  }

  if (dataType === FieldDataType.FileList) {
    ;(fields as unknown as IFieldReducers<IFileDetails[]>).value = (
      state: IFileDetails[] = [],
      action: Action<IProductFormActionTypesAndValues>
    ): IFileDetails[] => {
      // if (
      //   action.code !== code &&
      //   (!action.fields || action.fields[code] === undefined)
      // ) {
      //   return state
      // }
      switch (action.type) {
        case "RESET_FORM":
          return action.value.locked.includes(code) ? state : defaultValue
        case "DELETE_FILE":
          if (action.value.code !== code) {
            return state
          }
          const temp = state.filter(
            (pic: IFileDetails) =>
              pic.file.name !== action.value.fileInfos.file.name
          )
          if (
            temp.length > 0 &&
            !temp.find((pic: IFileDetails) => pic.isMain)
          ) {
            temp[0].isMain = true
          }
          return temp

        case "RESTORE_FILE":
          if (action.value.code !== code) {
            return state
          }
          return [
            ...state,
            {
              blob: action.value.fileInfos.path,
              file: { name: action.value.fileInfos.name } as any,
              isMain: !state.find((file: IFileDetails) => file.isMain),
              name: action.value.fileInfos.name,
              isNew: false,
              isRestored: true,
              path: action.value.fileInfos.path,
            },
          ]

        case "CREATE_FILE":
          if (action.value.code !== code) {
            return state
          }
          return [
            ...state,
            {
              blob: action.value.blob,
              file: action.value.file,
              isMain: !state.find((file: IFileDetails) => file.isMain),
              name: action.value.name,
              isNew: action.value.isNew,
              path: "",
            },
          ]

        case "STORE_PRODUCT":
          return action.value.values[code] !== undefined &&
            isEqual(state, defaultValue)
            ? action.value.values[code]
            : state

        case "CHANGE_FILE_POSITION":
          if (action.value.code !== code) {
            return state
          }

          const actualPos = state.findIndex(
            (i) => i.file.name === action.value.fileName
          )
          const newPos = actualPos + (action.value.increment ? -1 : 1)
          if (newPos >= state.length || newPos <= -1) {
            return state
          }
          let newState = arrayMove(state, actualPos, newPos)
          newState = newState.map((item) => ({ ...item, isMain: false }))
          newState[0].isMain = true
          return newState
        default:
          return state
      }
    }
  }

  // return combineReducers<typeof fields>(fields)

  const test = { ...fields }
  return combineReducers<typeof test>(test)
}

const getValueReducer = <T>(
  code: string,
  defaultValue: T,
  fieldType: FieldType,
  formFieldType: FormFieldTypes
): Reducer<T> => {
  return (
    state = defaultValue,
    action: Action<IProductFormActionTypesAndValues>
  ) => {
    if (
      action.type === "CHANGE_FIELD_VALUE" &&
      // (
      action.value.code !== code
      //  && (!action.value.fields || action.value.fields[code] === undefined)) ||
      // action.value.fieldType !== fieldType
    ) {
      return state
    }
    switch (action.type) {
      case "STORE_TECH_FIELDS":
        if (fieldType === FieldType.Tech && action.value.techValues) {
          const t = action.value.techValues.find((v) => v.codeEnum === code)
          if (t) {
            return formFieldType === FormFieldTypes.Dropdown
              ? t?.valueCode
              : t?.value
          }
        }
        return state
      case "RESET_FORM":
        return action.value.locked.includes(code) ? state : defaultValue
      case "CHANGE_FIELD_VALUE":
        if (formFieldType === FormFieldTypes.Checkbox) {
          return convertToBoolean(action.value.fieldValue)
        } else {
          return action.value.fieldValue
        }

      case "STORE_PRODUCT":
        return action.value.values[code] !== undefined &&
          isEqual(state, defaultValue)
          ? action.value.values[code]
          : state
      case "CHANGE_MODIFIED_STATE":
        return action.value.code === code && !action.value.modified
          ? defaultValue
          : state
      // return (action.value.code === code ||
      //   isTechAndMustModify(code, fieldType, action)) &&
      //   !action.value.modified
      //   ? defaultValue
      //   : state

      default:
        return state
    }
  }
}

export default createField

export const createFormFieldByCaratech = <T extends FieldDataType>(
  caratech: ITechRawData
): ICreateField<T> => {
  const options = caratech.options
    ? caratech.options.map(({ label, id, cle, code }) => ({
        text: label,
        key: id,
        value: id,
        cle,
      }))
    : []

  // console.log()

  return {
    code: caratech.codeEnum,
    label: caratech.label,
    dataType: patternDataTypeMatch[caratech.type] as T,
    defaultValue: getDefaultCaratechValue(caratech.type, caratech.isDeclinable),
    localized: caratech.type === CaratechType.text ? true : false,
    formFieldType: patternFormFieldTypeMatch[caratech.type],
    isRequired: caratech.isRequired,
    isDeclinable: caratech.isDeclinable,
    fieldType: FieldType.Tech,
    modified: false,
    lock: "unlock",
    options,
  }
}

const isTechAndMustLock = (
  code: string,
  fieldType: FieldType,
  action: Action<IProductFormActionTypesAndValues>
) =>
  action.type === "CHANGE_FIELD_VALUE" &&
  ((code === "techNode" &&
    action.value.fieldType === FieldType.Tech &&
    action.value.lock === "lock") ||
    (action.value.code === "techNode" &&
      fieldType === FieldType.Tech &&
      action.value.lock === "unlock"))

const isTechAndMustModify = (
  fieldType: FieldType,
  action: Action<IProductFormActionTypesAndValues>
) =>
  action.type === "CHANGE_MODIFIED_STATE" &&
  action.value.code === "techNode" &&
  fieldType === FieldType.Tech
